// Telas de acesso: login (com 2FA), esqueci a senha, definir senha (convite/recuperação) e o
// cadastro obrigatório do 2FA para quem configura.

import { useQueryClient } from "@tanstack/react-query";
import { KeyRound, ShieldCheck } from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import { Alert, Button, Card, Field, Input, Spinner } from "../components/ui";
import { api, ApiError } from "../lib/api";
import { safeNext } from "../lib/nav";
import { BrandMark, useApplyBranding, useLogout, useMe, usePublicBranding } from "../lib/session";

function AuthLayout({ children, footer }: { children: ReactNode; footer?: ReactNode }) {
  const { data: branding } = usePublicBranding();
  useApplyBranding(branding);
  return (
    <main className="auth-screen relative flex min-h-screen flex-col items-center justify-center px-4 pt-10 pb-16">
      <div className="mb-8">
        <BrandMark branding={branding} size="lg" />
      </div>
      <Card className="w-full max-w-sm p-6">{children}</Card>
      {footer && <div className="mt-6 text-center text-sm text-muted">{footer}</div>}
      <p className="mt-10 text-xs text-muted">GMeyer Insight · gestão de dados de excitação e regulação</p>
      {/* Na base da tela, sem tirar o cartão do centro. */}
      {/* Assinatura: escura no tema claro, negativa no escuro (troca por CSS, como o logo). */}
      <footer className="absolute inset-x-0 bottom-4 flex items-center justify-center gap-2 text-xs text-muted">
        <span>Developed by</span>
        <img src="/brand/softenge-reports.png" alt="Softenge Reports" className="brand-on-light h-10 w-auto" />
        <img src="/brand/softenge-reports-negativa.png" alt="Softenge Reports" className="brand-on-dark h-10 w-auto" />
      </footer>
    </main>
  );
}

function message(e: unknown): string {
  return e instanceof ApiError ? e.message : "não foi possível falar com o servidor";
}


// ── Login ────────────────────────────────────────────────────────────────────

export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const next = safeNext(params.get("next"));

  if (me) return <Navigate to={next} replace />;

  async function finish() {
    await qc.invalidateQueries({ queryKey: ["me"] });
    navigate(next, { replace: true });
  }

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ status: "ok" | "mfa"; challenge?: string }>("/api/auth/login", { email, password });
      if (r.status === "mfa") setChallenge(r.challenge!);
      else await finish();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/auth/login/mfa", { challenge, code });
      await finish();
    } catch (err) {
      if (err instanceof ApiError && err.code === "desafio_invalido") {
        setChallenge(null);
        setCode("");
      }
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }

  if (challenge) {
    return (
      <AuthLayout>
        <form onSubmit={submitCode} className="space-y-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-5 text-brand" aria-hidden />
            <h1 className="text-lg font-semibold">Verificação em duas etapas</h1>
          </div>
          <p className="text-sm text-muted">Digite o código de 6 dígitos do seu aplicativo autenticador.</p>
          {error && <Alert kind="error">{error}</Alert>}
          <Field label="Código">
            {(id) => (
              <Input
                id={id}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                pattern="\d{6}"
                className="text-center text-lg tracking-[0.4em]"
              />
            )}
          </Field>
          <Button type="submit" className="w-full" loading={busy} disabled={code.length !== 6}>
            Verificar
          </Button>
          <button
            type="button"
            className="w-full text-sm text-muted hover:text-fg"
            onClick={() => {
              setChallenge(null);
              setCode("");
              setError(null);
            }}
          >
            Voltar
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout footer={<Link to="/senha/esqueci" className="hover:text-fg">Esqueci minha senha</Link>}>
      <form onSubmit={submitPassword} className="space-y-4">
        <h1 className="text-lg font-semibold">Entrar</h1>
        {error && <Alert kind="error">{error}</Alert>}
        <Field label="E-mail">
          {(id) => (
            <Input id={id} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          )}
        </Field>
        <Field label="Senha">
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          )}
        </Field>
        <Button type="submit" className="w-full" loading={busy}>
          Entrar
        </Button>
      </form>
    </AuthLayout>
  );
}

// ── Esqueci a senha ──────────────────────────────────────────────────────────

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/auth/password/forgot", { email });
      setSent(true);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout footer={<Link to="/login" className="hover:text-fg">Voltar para o login</Link>}>
      {sent ? (
        <div className="space-y-3">
          <h1 className="text-lg font-semibold">Verifique seu e-mail</h1>
          <p className="text-sm text-muted">
            Se <strong className="text-fg">{email}</strong> tiver acesso à plataforma, enviamos um link para definir uma nova
            senha. Ele vale por 2 horas.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <h1 className="text-lg font-semibold">Recuperar senha</h1>
          <p className="text-sm text-muted">Informe seu e-mail e enviaremos um link para definir uma nova senha.</p>
          {error && <Alert kind="error">{error}</Alert>}
          <Field label="E-mail">
            {(id) => <Input id={id} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />}
          </Field>
          <Button type="submit" className="w-full" loading={busy}>
            Enviar link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

// ── Definir senha (convite ou recuperação) ───────────────────────────────────

export function SetPasswordPage({ mode }: { mode: "invite" | "reset" }) {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mismatch = confirm.length > 0 && confirm !== password;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (mismatch) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/auth/password/set", { token, password });
      setDone(true);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout footer={<Link to="/login" className="hover:text-fg">Ir para o login</Link>}>
        <Alert kind="error">Link incompleto. Abra o link exatamente como veio no e-mail.</Alert>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout>
        <div className="space-y-4">
          <Alert kind="ok">Senha definida.</Alert>
          <Link to="/login">
            <Button className="w-full">Entrar</Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form onSubmit={submit} className="space-y-4">
        <div className="flex items-center gap-2">
          <KeyRound className="size-5 text-brand" aria-hidden />
          <h1 className="text-lg font-semibold">{mode === "invite" ? "Criar sua senha" : "Nova senha"}</h1>
        </div>
        {error && <Alert kind="error">{error}</Alert>}
        <Field label="Senha" hint="Pelo menos 10 caracteres. Uma frase curta é mais fácil de lembrar e mais forte.">
          {(id) => (
            <Input id={id} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={10} autoFocus />
          )}
        </Field>
        <Field label="Confirme a senha" error={mismatch ? "as senhas não conferem" : null}>
          {(id) => <Input id={id} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />}
        </Field>
        <Button type="submit" className="w-full" loading={busy} disabled={mismatch || password.length < 10}>
          Salvar senha
        </Button>
      </form>
    </AuthLayout>
  );
}

// ── Troca da senha temporária ────────────────────────────────────────────────

/**
 * Primeiro acesso com a senha temporária dada pelo administrador: a troca é obrigatória
 * (a API recusa o resto até lá). Pede a temporária de novo — prova de que é o próprio
 * usuário, e não uma sessão esquecida aberta.
 */
export function ChangeTemporaryPasswordPage() {
  const { data: me, isLoading } = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const logout = useLogout();
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mismatch = confirm.length > 0 && confirm !== password;

  if (isLoading) return <Spinner />;
  if (!me) return <Navigate to="/login" replace />;
  if (!me.mustChangePassword) return <Navigate to="/" replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (mismatch) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/me/password", { current, password });
      await qc.invalidateQueries({ queryKey: ["me"] });
      navigate("/", { replace: true });
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      footer={
        <button type="button" onClick={logout} className="hover:text-fg">
          Sair
        </button>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="flex items-center gap-2">
          <KeyRound className="size-5 text-brand" aria-hidden />
          <h1 className="text-lg font-semibold">Crie a sua senha</h1>
        </div>
        <p className="text-sm text-muted">Você entrou com uma senha temporária. Troque por uma senha só sua para continuar.</p>
        {error && <Alert kind="error">{error}</Alert>}
        <Field label="Senha temporária">
          {(id) => (
            <Input id={id} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required autoFocus />
          )}
        </Field>
        <Field label="Nova senha" hint="Pelo menos 10 caracteres. Uma frase curta é mais fácil de lembrar e mais forte.">
          {(id) => (
            <Input id={id} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={10} />
          )}
        </Field>
        <Field label="Confirme a nova senha" error={mismatch ? "as senhas não conferem" : null}>
          {(id) => <Input id={id} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />}
        </Field>
        <Button type="submit" className="w-full" loading={busy} disabled={mismatch || password.length < 10 || !current}>
          Salvar e continuar
        </Button>
      </form>
    </AuthLayout>
  );
}

// ── Cadastro do 2FA ──────────────────────────────────────────────────────────

/** Formulário do 2FA (QR + confirmação). Usado no primeiro acesso e no Perfil. */
export function MfaEnroll({ onDone }: { onDone: () => void }) {
  const [setup, setSetup] = useState<{ secret: string; qr: string } | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Uma chamada só: cada /setup gera um segredo novo no servidor. Com duas (o React roda o
  // efeito duas vezes em StrictMode), o QR exibido podia ser o da primeira e o gravado o da
  // segunda — o código do autenticador nunca bateria.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api
      .post<{ secret: string; qr: string }>("/api/me/mfa/setup")
      .then(setSetup)
      .catch((e) => setError(message(e)));
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/me/mfa/enable", { code });
      onDone();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }

  if (!setup && !error) return <Spinner label="Gerando o código" />;

  return (
    <form onSubmit={submit} className="space-y-4">
      {error && <Alert kind="error">{error}</Alert>}
      {setup && (
        <>
          <ol className="list-decimal space-y-1 pl-5 text-sm text-muted">
            <li>Abra o aplicativo autenticador (Google Authenticator, Microsoft Authenticator…).</li>
            <li>Escaneie o QR code abaixo.</li>
            <li>Digite o código de 6 dígitos que aparecer.</li>
          </ol>
          <div className="flex justify-center rounded-md bg-white p-3">
            <img src={setup.qr} alt="QR code para o aplicativo autenticador" className="size-48" />
          </div>
          <details className="text-xs text-muted">
            <summary className="cursor-pointer">Não consegue escanear? Digite a chave</summary>
            <code className="mt-2 block break-all rounded bg-surface-2 p-2 font-mono text-fg">{setup.secret}</code>
          </details>
          <Field label="Código de 6 dígitos">
            {(id) => (
              <Input
                id={id}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                className="text-center text-lg tracking-[0.4em]"
                required
              />
            )}
          </Field>
          <Button type="submit" className="w-full" loading={busy} disabled={code.length !== 6}>
            Ativar
          </Button>
        </>
      )}
    </form>
  );
}

export function MfaSetupPage() {
  const { data: me, isLoading } = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const logout = useLogout();

  if (isLoading) return null;
  if (!me) return <Navigate to="/login" replace />;
  if (!me.mfaSetupRequired) return <Navigate to="/" replace />;

  return (
    <AuthLayout footer={<button type="button" onClick={logout} className="hover:text-fg">Sair</button>}>
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="size-5 text-brand" aria-hidden />
          <h1 className="text-lg font-semibold">Proteja sua conta</h1>
        </div>
        <p className="text-sm text-muted">
          Como administrador da plataforma você acessa todos os grupos, então a verificação em duas etapas é obrigatória. Leva um minuto.
        </p>
        <MfaEnroll
          onDone={async () => {
            await qc.invalidateQueries({ queryKey: ["me"] });
            navigate("/", { replace: true });
          }}
        />
      </div>
    </AuthLayout>
  );
}
